class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * Desktop nav: top-level items use real links + hover/focus-within submenus (no <details> on parent).
 * close() is still called when the sticky header hides on scroll.
 */
class HeaderMenu extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this._clearSuppress = this.clearSuppress.bind(this);
    this._onMegaPosition = this._onMegaPosition.bind(this);
    this._setPreventHideTrue = this._setPreventHide.bind(this, true);
    this._setPreventHideFalse = this._setPreventHide.bind(this, false);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onFlyoutTriggerEnter = this._onFlyoutTriggerEnter.bind(this);
    this._onFlyoutContainerEnter = this._onFlyoutContainerEnter.bind(this);
    this._onFlyoutContainerLeave = this._onFlyoutContainerLeave.bind(this);
    this._flyoutLeaveTimer = null;
    this._flyoutLeaveDelayMs = 300;
  }

  connectedCallback() {
    this.addEventListener('mouseenter', this._clearSuppress);
    this.addEventListener('focusin', this._clearSuppress);

    this.addEventListener('mouseenter', this._setPreventHideTrue);
    this.addEventListener('mouseleave', this._setPreventHideFalse);
    this.addEventListener('focusin', this._setPreventHideTrue);
    this.addEventListener('focusout', this._onFocusOut);

    if (this.classList.contains('header-menu--mega')) {
      this.addEventListener('mouseenter', this._onMegaPosition);
      this.addEventListener('focusin', this._onMegaPosition);
    }

    if (this.classList.contains('header-menu--dropdown') || this.classList.contains('header-menu--mega')) {
      const trigger = this.querySelector(':scope > a.header__menu-item');
      if (trigger) {
        trigger.addEventListener('mouseenter', this._onFlyoutTriggerEnter);
      }
      this.addEventListener('mouseenter', this._onFlyoutContainerEnter);
      this.addEventListener('mouseleave', this._onFlyoutContainerLeave);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('mouseenter', this._clearSuppress);
    this.removeEventListener('focusin', this._clearSuppress);

    this.removeEventListener('mouseenter', this._setPreventHideTrue);
    this.removeEventListener('mouseleave', this._setPreventHideFalse);
    this.removeEventListener('focusin', this._setPreventHideTrue);
    this.removeEventListener('focusout', this._onFocusOut);

    if (this.classList.contains('header-menu--mega')) {
      this.removeEventListener('mouseenter', this._onMegaPosition);
      this.removeEventListener('focusin', this._onMegaPosition);
    }

    const trigger = this.querySelector(':scope > a.header__menu-item');
    if (trigger) {
      trigger.removeEventListener('mouseenter', this._onFlyoutTriggerEnter);
    }
    this.removeEventListener('mouseenter', this._onFlyoutContainerEnter);
    this.removeEventListener('mouseleave', this._onFlyoutContainerLeave);
    if (this._flyoutLeaveTimer) {
      clearTimeout(this._flyoutLeaveTimer);
      this._flyoutLeaveTimer = null;
    }
  }

  _onFlyoutTriggerEnter() {
    if (this._flyoutLeaveTimer) {
      clearTimeout(this._flyoutLeaveTimer);
      this._flyoutLeaveTimer = null;
    }
    this.classList.add('header-menu--flyout-active');
  }

  _onFlyoutContainerEnter() {
    if (this._flyoutLeaveTimer) {
      clearTimeout(this._flyoutLeaveTimer);
      this._flyoutLeaveTimer = null;
    }
  }

  _onFlyoutContainerLeave() {
    this._flyoutLeaveTimer = setTimeout(() => {
      this.classList.remove('header-menu--flyout-active');
      this._flyoutLeaveTimer = null;
    }, this._flyoutLeaveDelayMs);
  }

  /** Close hover flyout immediately (e.g. when pointer leaves the whole header wrapper — keeps bar + menu in sync). */
  collapseFlyoutPointer() {
    if (this._flyoutLeaveTimer) {
      clearTimeout(this._flyoutLeaveTimer);
      this._flyoutLeaveTimer = null;
    }
    this.classList.remove('header-menu--flyout-active');
  }

  clearSuppress() {
    this.classList.remove('header-menu--suppress-open');
  }

  _setPreventHide(value) {
    if (!this.header) this.header = document.querySelector('.header-wrapper');
    if (this.header) this.header.preventHide = value;
  }

  _onFocusOut(event) {
    if (this.contains(event.relatedTarget)) return;
    this._setPreventHideFalse();
  }

  _onMegaPosition() {
    if (!this.header) this.header = document.querySelector('.header-wrapper');
    if (!this.header) return;
    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  close() {
    this.classList.add('header-menu--suppress-open');
    if (this._flyoutLeaveTimer) {
      clearTimeout(this._flyoutLeaveTimer);
      this._flyoutLeaveTimer = null;
    }
    this.classList.remove('header-menu--flyout-active');
    this._setPreventHide(false);
    this.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'));
  }
}

customElements.define('header-menu', HeaderMenu);
